/**
 * Centralized model configuration for TAIS frontend
 * 
 * This module manages the configuration of AI models from different providers.
 * It provides:
 * - Model definitions and metadata
 * - Provider-specific settings
 * - Model selection utilities
 * - UI display information
 */

export enum ModelProvider {
  OPENAI = 'openai',
  ANTHROPIC = 'anthropic'
}

export interface ModelConfig {
  id: string;
  name: string;
  displayName: string;
  provider: ModelProvider;
  description?: string;
  maxTokens?: number;
  contextWindow?: number;
  capabilities?: string[];
  isRecommended?: boolean;
  isNew?: boolean;
  pricing?: {
    inputPerMillion?: number;
    outputPerMillion?: number;
  };
}

// OpenAI Models
export const OPENAI_MODELS: Record<string, ModelConfig> = {
  'gpt-5': {
    id: 'gpt-5',
    name: 'gpt-5',
    displayName: 'GPT-5',
    provider: ModelProvider.OPENAI,
    description: 'Most advanced OpenAI model with enhanced reasoning',
    maxTokens: 8192,
    contextWindow: 200000,
    capabilities: ['text', 'advanced-reasoning', 'function-calling'],
    isNew: true,
    isRecommended: true
  },
  'gpt-5-mini': {
    id: 'gpt-5-mini',
    name: 'gpt-5-mini',
    displayName: 'GPT-5 Mini',
    provider: ModelProvider.OPENAI,
    description: 'Faster, cost-effective GPT-5 variant',
    maxTokens: 8192,
    contextWindow: 128000,
    capabilities: ['text', 'fast-inference'],
    isNew: true
  },
  'gpt-5-nano': {
    id: 'gpt-5-nano',
    name: 'gpt-5-nano',
    displayName: 'GPT-5 Nano',
    provider: ModelProvider.OPENAI,
    description: 'Ultra-fast, lightweight GPT-5 for simple tasks',
    maxTokens: 4096,
    contextWindow: 64000,
    capabilities: ['text', 'ultra-fast'],
    isNew: true
  }
};

// Anthropic Claude Models
export const ANTHROPIC_MODELS: Record<string, ModelConfig> = {
  'claude-4-sonnet': {
    id: 'claude-4-sonnet',
    name: 'claude-4-sonnet',
    displayName: 'Claude 4 Sonnet',
    provider: ModelProvider.ANTHROPIC,
    description: 'Latest Claude model with enhanced capabilities',
    maxTokens: 8192,
    contextWindow: 400000,
    capabilities: ['text', 'advanced-reasoning', 'enhanced-analysis'],
    isNew: true,
    isRecommended: true
  },
  'claude-4-5-sonnet': {
    id: 'claude-4-5-sonnet',
    name: 'claude-4-5-sonnet',
    displayName: 'Claude 4.5 Sonnet',
    provider: ModelProvider.ANTHROPIC,
    description: 'Next-generation Claude 4.5 with advanced reasoning and analysis',
    maxTokens: 8192,
    contextWindow: 400000,
    capabilities: ['text', 'advanced-reasoning', 'enhanced-analysis', 'improved-safety'],
    isNew: true,
    isRecommended: true
  },
  'claude-4-5-haiku': {
    id: 'claude-4-5-haiku',
    name: 'claude-4-5-haiku',
    displayName: 'Claude 4.5 Haiku',
    provider: ModelProvider.ANTHROPIC,
    description: 'Fast and efficient Claude 4.5 for quick responses',
    maxTokens: 8192,
    contextWindow: 200000,
    capabilities: ['text', 'fast-inference', 'cost-effective'],
    isNew: true
  }
};

// Combined model registry
export const MODEL_REGISTRY: Record<string, ModelConfig> = {
  ...OPENAI_MODELS,
  ...ANTHROPIC_MODELS
};

// Provider information
export const PROVIDER_INFO: Record<ModelProvider, { name: string; description: string; color: string }> = {
  [ModelProvider.OPENAI]: {
    name: 'OpenAI',
    description: 'Advanced language models with multimodal capabilities',
    color: '#10A37F'
  },
  [ModelProvider.ANTHROPIC]: {
    name: 'Anthropic',
    description: 'Safe, steerable AI with constitutional training',
    color: '#D4A574'
  }
};

// Utility functions
export const getModelConfig = (modelId: string): ModelConfig | undefined => {
  return MODEL_REGISTRY[modelId];
};

export const getModelsByProvider = (provider: ModelProvider): ModelConfig[] => {
  return Object.values(MODEL_REGISTRY).filter(model => model.provider === provider);
};

export const getRecommendedModels = (): ModelConfig[] => {
  return Object.values(MODEL_REGISTRY).filter(model => model.isRecommended);
};

export const getNewModels = (): ModelConfig[] => {
  return Object.values(MODEL_REGISTRY).filter(model => model.isNew);
};

export const getAllModelIds = (): string[] => {
  return Object.keys(MODEL_REGISTRY);
};

export const getAllModels = (): ModelConfig[] => {
  return Object.values(MODEL_REGISTRY);
};

// Model selection options for UI components
export const getModelSelectOptions = () => {
  return Object.values(MODEL_REGISTRY).map(model => ({
    value: model.id,
    label: model.displayName,
    provider: model.provider,
    description: model.description,
    isRecommended: model.isRecommended,
    isNew: model.isNew
  }));
};

// Group models by provider for organized display
export const getModelsByProviderGrouped = () => {
  const grouped: Record<ModelProvider, ModelConfig[]> = {
    [ModelProvider.OPENAI]: [],
    [ModelProvider.ANTHROPIC]: []
  };

  Object.values(MODEL_REGISTRY).forEach(model => {
    grouped[model.provider].push(model);
  });

  return grouped;
};

// Default model configurations
export const DEFAULT_MODEL = 'claude-4-sonnet';
export const DEFAULT_TEMPERATURE = 0.5;
export const DEFAULT_MAX_TOKENS = 8192;

// Model validation
export const isValidModel = (modelId: string): boolean => {
  return modelId in MODEL_REGISTRY;
};

// Get model display information
export const getModelDisplayInfo = (modelId: string) => {
  const model = getModelConfig(modelId);
  if (!model) return null;

  const provider = PROVIDER_INFO[model.provider];
  
  return {
    ...model,
    providerInfo: provider,
    fullDisplayName: `${model.displayName} (${provider.name})`
  };
};
